/* custom code start */
if (!customElements.get("home-featured-products")) {
  class HomeFeaturedProducts extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll(".hfp-tab"));
      this.swiperEl = this.querySelector("swiper-component");
      this.slider = this.querySelector(".hfp-slider");
      this.slides = Array.from(this.querySelectorAll(".hfp-slide"));
      this.progressBar = this.querySelector(".hfp-progress__bar");

      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.updateProgress = this.updateProgress.bind(this);

      this.addEventListener("shopify:block:select", this.onBlockSelect);

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.onTabClick);
        tab.addEventListener("keydown", this.onTabKeydown);
      });

      // Swiper boots asynchronously (global.js, on load / when visible) — wait for it
      this.whenSwiperReady((swiper) => {
        this.applyGap(swiper);
        swiper.on("setTranslate", this.updateProgress);
        swiper.on("slideChange", this.updateProgress);
        swiper.on("resize", this.updateProgress);
        this.updateProgress();
      });

      const active = this.tabs.find((t) => t.classList.contains("is-active")) || this.tabs[0];
      if (active) this.activate(active, { animate: false });
    }

    disconnectedCallback() {
      this.removeEventListener("shopify:block:select", this.onBlockSelect);
      this.tabs.forEach((tab) => {
        tab.removeEventListener("click", this.onTabClick);
        tab.removeEventListener("keydown", this.onTabKeydown);
      });
      this._swiperWait = false;
    }

    getSwiper() {
      if (!this.swiperEl) return null;
      return this.swiperEl.swiper || this.swiperEl.swiperInstance || null;
    }

    whenSwiperReady(cb, tries = 120) {
      this._swiperWait = true;
      const swiper = this.getSwiper();
      if (swiper) return cb(swiper);
      if (tries <= 0 || this._swiperWait === false) return;
      setTimeout(() => this.whenSwiperReady(cb, tries - 1), 50);
    }

    // CSS owns the gap (--hfp-gap); hand it to Swiper so spaceBetween stays in one place
    applyGap(swiper) {
      if (!this.slider) return;
      const raw = getComputedStyle(this.slider).getPropertyValue("--hfp-gap").trim();
      if (!raw) return;
      const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      let gap = parseFloat(raw) || 0;
      if (raw.endsWith("rem")) gap *= rootPx;

      swiper.params.spaceBetween = gap;
      const bps = swiper.params.breakpoints;
      if (bps) {
        [750, 990].forEach((bp) => {
          if (bps[bp]) bps[bp].spaceBetween = gap;
        });
      }
      swiper.update();
    }

    onTabClick(event) {
      this.activate(event.currentTarget, { animate: true });
    }

    onTabKeydown(event) {
      const index = this.tabs.indexOf(event.currentTarget);
      let next = null;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          next = this.tabs[(index + 1) % this.tabs.length];
          break;
        case "ArrowLeft":
        case "ArrowUp":
          next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
          break;
        case "Home":
          next = this.tabs[0];
          break;
        case "End":
          next = this.tabs[this.tabs.length - 1];
          break;
        default:
          return;
      }

      event.preventDefault();
      next.focus();
      this.activate(next, { animate: true });
    }

    onBlockSelect(event) {
      const slide = event.target.closest(".hfp-slide");
      if (!slide) return;
      const tabId = slide.dataset.tab;
      const tab = this.tabs.find((t) => t.dataset.tab === tabId);
      if (tab) {
        this.activate(tab, { animate: true, targetSlide: slide });
      } else {
        this.slideTo(slide, true);
      }
    }

    activate(tab, { animate = true, targetSlide = null } = {}) {
      const tabId = tab.dataset.tab;

      this.tabs.forEach((t) => {
        const isActive = t === tab;
        t.classList.toggle("is-active", isActive);
        t.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      // With a single tab there is no data-tab filtering to do
      if (this.tabs.length > 1) {
        this.slides.forEach((slide) => {
          slide.classList.toggle("hfp-slide--hidden", slide.dataset.tab !== tabId);
        });
      }

      const swiper = this.getSwiper();
      if (swiper) swiper.update();

      if (targetSlide) {
        this.slideTo(targetSlide, animate);
      } else if (swiper) {
        swiper.slideTo(0, animate ? 300 : 0);
      }

      this.updateProgress();
    }

    slideTo(slide, animate) {
      const swiper = this.getSwiper();
      if (!swiper) return;
      const index = Array.prototype.indexOf.call(swiper.slides, slide);
      swiper.slideTo(index >= 0 ? index : 0, animate ? 300 : 0);
    }

    updateProgress() {
      if (!this.progressBar) return;
      const swiper = this.getSwiper();
      if (!swiper || !swiper.enabled) return;

      // Fill effect: bar is anchored left and grows as you scroll.
      // Starts at the viewport fraction (ratio) and fills to 100% at the end.
      const ratio = Math.min(swiper.width / swiper.virtualSize, 1) || 1;
      const fill = ratio + (1 - ratio) * (swiper.progress || 0);
      this.progressBar.style.width = `${fill * 100}%`;
    }
  }

  customElements.define("home-featured-products", HomeFeaturedProducts);
}
/* custom code end */
